import { Button, Center, Icon, Spinner, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { FaFileDownload } from "react-icons/fa";

export const PdfPreview = ({
  source,
  initialPage,
  onDownload,
}: {
  source: string | Blob | undefined;
  initialPage?: number;
  onDownload?: () => void;
}) => {
  const { objectUrl, hasFailed } = usePdfObjectUrl(source);

  if (hasFailed)
    return (
      <Center width="full" height="full" flexDirection="column" gap="3" px="6" textAlign="center">
        <Text fontSize="sm">{t("Cannot preview this document. Download it to open it.")}</Text>
        {onDownload && (
          <Button size="sm" variant="outline" leftIcon={<Icon as={FaFileDownload} />} onClick={onDownload}>
            {t("Download")}
          </Button>
        )}
      </Center>
    );

  if (!objectUrl)
    return (
      <Center width="full" height="full">
        <Spinner color="smPrimary" />
      </Center>
    );

  return (
    <iframe
      src={initialPage && initialPage > 0 ? `${objectUrl}#page=${Math.floor(initialPage)}` : objectUrl}
      title={t("PDF preview") ?? ""}
      style={{ width: "100%", height: "100%", border: 0 }}
    />
  );
};

const usePdfObjectUrl = (source: string | Blob | undefined) => {
  const [objectUrl, setObjectUrl] = useState<string>();
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    setObjectUrl(undefined);
    setHasFailed(false);
    if (!source) return;

    let createdUrl: string | undefined;
    let isCurrent = true;

    const createObjectUrl = async () => {
      try {
        const pdf = typeof source === "string" ? await fetchPdf(source) : source;
        if (!isCurrent) return;
        createdUrl = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
        setObjectUrl(createdUrl);
      } catch {
        if (isCurrent) setHasFailed(true);
      }
    };

    createObjectUrl();

    return () => {
      isCurrent = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [source]);

  return { objectUrl, hasFailed };
};

const fetchPdf = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) throw Error(`Failed to fetch PDF, status ${response.status}`);
  return response.blob();
};
